import type {N8nNode} from './types';
import {
  ACTION_VERB_MAP,
  OPERATION_VERB_MAP,
  SUBTITLE_OVERRIDE_MAP,
} from './constants';
import {getCapability} from '../../capabilities/registry';

const AI_EVAL_REGEX = /evaluat|assess|analyz|score|classification|quality/i;

const ACTION_VERBS = [
  'Get',
  'Fetch',
  'Send',
  'Update',
  'Create',
  'Delete',
  'Find',
  'Parse',
  'Format',
  'Set',
  'Define',
  'Evaluate',
  'Analyze',
  'Generate',
  'Transform',
  'Process',
  'Filter',
  'Merge',
];

const ACTION_VERB_REGEX = new RegExp(`^(${ACTION_VERBS.join('|')})\\s+`, 'i');

const pipe =
  (...fns: Array<(s: string) => string>) =>
  (input: string): string =>
    fns.reduce((acc, fn) => fn(acc), input);

const stripRawN8nType =
  (nodeType: string) =>
  (name: string): string =>
    name === nodeType || name.startsWith('n8n-nodes-') ? '' : name;

const stripLeadingVerb =
  (actionVerb: string) =>
  (name: string): string => {
    if (!name) return name;
    const lower = name.toLowerCase();
    const verbLower = actionVerb.toLowerCase();
    if (lower.startsWith(verbLower + ' ')) {
      return name.slice(actionVerb.length + 1);
    }
    return name.replace(ACTION_VERB_REGEX, '');
  };

const stripParentheticals = (name: string): string =>
  name.replace(/\s*\([^)]*\)$/g, '').trim();

const limitWords =
  (max: number) =>
  (name: string): string => {
    const words = name.split(/\s+/).filter((w) => w.length > 0);
    return words.length > max ? words.slice(0, max).join(' ') : name;
  };

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

const humanizeShortType = (nodeType: string): string => {
  const short = nodeType.split('.').pop() ?? '';
  return capitalize(short.replace(/([A-Z])/g, ' $1').trim());
};

const fallbackFromParams =
  (params: Record<string, unknown> | undefined, nodeType: string) =>
  (name: string): string => {
    if (name && name.length >= 3) return name;

    if (params?.resource) {
      return capitalize(String(params.resource));
    }
    if (params?.operation) {
      return capitalize(String(params.operation));
    }

    return humanizeShortType(nodeType);
  };

/**
 * Resolves a dynamic action verb for a node.
 *
 * Resolution order (all O(1) except regex which is a single test):
 * 1. Exact match by n8n short type from ACTION_VERB_MAP
 * 2. Parameter-aware lookup from OPERATION_VERB_MAP
 * 3. Name-based regex for AI evaluation patterns
 * 4. Static fallback from capability registry
 * 5. Ultimate default: "Process"
 */
export const resolveActionVerb = (
  node: N8nNode,
  capability: string
): string => {
  const shortType = node.type.split('.').pop() ?? '';

  const exactVerb = ACTION_VERB_MAP.get(shortType);
  if (exactVerb) return exactVerb;

  const operation = (node.parameters?.operation as string | undefined) ?? '';
  if (operation) {
    const operationVerb = OPERATION_VERB_MAP.get(operation);
    if (operationVerb) return operationVerb;
  }

  if (AI_EVAL_REGEX.test(node.name)) return 'Evaluate';

  return getCapability(capability)?.label ?? 'Process';
};

/**
 * Resolves a cleaned subtitle for display from the raw n8n node name.
 *
 * Pipeline: trim -> strip raw type -> strip leading verb -> strip parentheticals
 *           -> limit words -> fallback from params
 */
export const resolveSubtitle = (node: N8nNode, actionVerb: string): string => {
  const shortType = node.type.split('.').pop() ?? '';
  const override = SUBTITLE_OVERRIDE_MAP.get(shortType);
  if (override) return override;

  return pipe(
    (s: string) => s.trim(),
    stripRawN8nType(node.type),
    stripLeadingVerb(actionVerb),
    stripParentheticals,
    limitWords(5),
    fallbackFromParams(node.parameters, node.type)
  )(node.name);
};
